import { Alert, Box, LinearProgress, Typography } from "@mui/material";
import { useDispatch } from "react-redux";
import { useEffect, useState } from "react";
import { format, parseISO } from "date-fns";
import { MyListApi } from "../utils/restApi";
import { addNotification } from "../slices/notificationSlice";
import { ItemCard, ItemIdentity, StatusBadge } from "./ui/ItemCard";
import { formatLabel, getItemImageUrl } from "../utils/itemUtil";

// Upper bound of the single collection request (unchanged from before).
const COLLECTION_PAGE_SIZE = 900;

const safeDate = value => {
    try {
        const d = parseISO(value);
        return Number.isNaN(d.getTime()) ? null : d;
    } catch (e) {
        return null;
    }
}

// Profile collection card: the person's own actions on an item.
const CollectionCard = ({ item, highlightAction }) => {
    const actions = item.actions || [];
    const dated = actions
        .map(a => ({ ...a, date: safeDate(a.createdAt) }))
        .filter(a => a.date)
        .sort((a, b) => b.date - a.date);
    const shown = (highlightAction && dated.find(a => a.action === highlightAction)) || dated[0];

    return (
        <ItemCard imageUrl={getItemImageUrl(item)} imageAlt={item.name ? `Image for ${item.name}` : ''} mediaResetKey={item.id}>
            <ItemIdentity item={item} titleComponent="h3" titleVariant="h3">
                {actions.map((a, ix) => (
                    <StatusBadge
                        key={`${a.action}-${ix}`}
                        sx={a.action === highlightAction ? { outline: '1px solid', outlineColor: 'sage.main' } : {}}
                    >
                        {formatLabel(a.action)}
                    </StatusBadge>
                ))}
            </ItemIdentity>
            {shown ?
                <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: '14px', mb: 0 }}>
                    {formatLabel(shown.action)} {format(shown.date, 'MMM d, yyyy')}
                </Typography>
            : null}
        </ItemCard>
    );
}

export const UserItems = ({ userId, tag, action }) => {

    const dispatch = useDispatch();

    const [items, setItems] = useState([]);
    const [status, setStatus] = useState('idle');

    // Reload whenever the profile changes so another person's collection
    // never lingers; filtering happens locally.
    useEffect(() => {
        let cancelled = false;
        setStatus('loading');
        setItems([]);
        MyListApi.getItems(userId, null, null, null, false, 0, COLLECTION_PAGE_SIZE)
            .then(data => {
                if (cancelled) return;
                setItems(data?.data?.items || []);
                setStatus('loaded');
            })
            .catch(error => {
                if (cancelled) return;
                dispatch(addNotification({
                    message: error.message,
                    type: 'error',
                }));
                setStatus('error');
            });
        return () => { cancelled = true; };
    }, [userId, dispatch]);

    if (status === 'loading' || status === 'idle') {
        return <LinearProgress aria-label="Loading collection" />;
    }

    if (status === 'error') {
        return <Alert severity="error">This collection couldn't be loaded.</Alert>;
    }

    const visible = items.filter(i => {
        if (tag && tag !== i.tag?.name) return false;
        if (action && !(i.actions || []).some(a => a.action === action)) return false;
        return true;
    });

    return (
        <Box>
            <Typography variant="caption" component="p" color="text.secondary" aria-live="polite" sx={{ mt: 0, mb: '12px' }}>
                {`${visible.length} item${visible.length === 1 ? '' : 's'}`}
            </Typography>
            {visible.length ?
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    {visible.map(i => <CollectionCard key={i.id} item={i} highlightAction={action} />)}
                </Box>
                : <Typography color="text.secondary">{items.length ? 'Nothing matches these filters.' : 'Nothing in this collection yet.'}</Typography>
            }
        </Box>
    );
}
